import { CommandResult, FormApiService, ItemCommandResult, ODataResult, QueryApiService } from 'src/common/api-service';
import { COMPLETION_SELECT } from 'src/api/profile-completion';
import { Expand, Filter, QueryOptions, Select } from 'odata-query';
import {
  AddEnterpriseUserCommandBody,
  CreateProjectWithRequestsBody,
  EnterpriseProfile,
  EnterpriseUser,
  EnterpriseUserDto,
  Project,
  ServiceRequestNotificationFrequency,
  ServiceRequest,
  ServiceRequestClosePreview,
  CloseServiceRequestBody,
  UpdateEnterpriseProfileInfoBody,
  UpdateEnterpriseUserProfileInfoBody,
  UpdateServiceRequestDto,
  UpsertEnterpriseBasicInfoCommand,
  UpdateProjectBody,
  EnterpriseReview,
  EnterpriseCancelAccountReason,
  UpsertProjectBody,
  DiscardCandidateForServiceRequestBody,
  ServiceRequestSkillDto,
  ServiceRequestJobRoleSkill
} from './types';
import { AxiosError } from 'axios';
import { RcFile } from 'antd/es/upload';
import { MatchStatusGroup, MatchPersonGroup } from '../match/types';

export const enterpriseServiceRequestSelect = [
  'id',
  'createdOn',
  'createdById',
  'createdByDisplayName',
  'modifiedOn',
  'modifiedById',
  'modifiedByDisplayName',
  'projectId',
  'roleId',
  'roleExperience',
  'location',
  'locationWorkPreference',
  'tentativeStartDate',
  'serviceDurationInMonths',
  'tentativeEndDate',
  'description',
  'isDraft',
  'notificationsFrequency',
  'isActive',
  'publishDate',
  'state',
  'newMatchesCount',
  'acceptedMatchesCount',
  'matchMeetingsCount',
  'rejectedMatchesCount',
  'selectedMatchesCount',
  'contractsCount',
  'newMatchesAddedOn',
  'doNotAskDiscardServiceComplete',
  'doNotAskDiscardBlockBeforeMeeting'
] as any;

const serviceRequestSelectWithExpandedFields = (expand?: Expand<ServiceRequest>) => {
  const select = [...enterpriseServiceRequestSelect];
  const expandFields = Object.keys((expand ?? {}) as Record<string, unknown>);

  expandFields.forEach((field) => {
    if (!select.includes(field)) select.push(field);
  });

  return select as any;
};

const withSafeServiceRequestSelect = <T extends { select?: Select<ServiceRequest>; expand?: Expand<ServiceRequest> }>(options: T): T => {
  if (options.select != null) return options;

  return {
    ...options,
    select: serviceRequestSelectWithExpandedFields(options.expand)
  };
};

export class EnterpriseProfileApiService extends QueryApiService<EnterpriseProfile> {
  constructor() {
    super('enterpriseProfiles');
  }

  public getProfile(
    options: { select?: Select<EnterpriseProfile>; expand?: Expand<EnterpriseProfile> } = {
      select: [
        'id',
        'name',
        'websiteUrl',
        'industryArea',
        'description',
        'enterpriseSize',
        'adminUserId',
        'profileImageFileId',
        'bannerImageFileId',
        'industryAreaId',
        'ratingAverages',
        'linkedInUrl',
        'originDraftShortlistId',
        'onboardingDraftJson'
      ],
      expand: {
        industryArea: {
          select: ['id', 'name'] as any
        },
        enterpriseUsers: {
          select: [
            'id',
            'userIdentityId',
            'fullName',
            'phoneNumber',
            'isAdmin',
            'registrationCompleted',
            'active',
            'profileImageFileId',
            'receiveEmailNotifications',
            'rejectedCancellationRequestComment',
            'cancellationRequestStatus',
            'onboardingCompleted',
            ...COMPLETION_SELECT
          ],
          expand: {
            contactAgent: {
              select: ['id', 'email', 'fullName', 'phoneNumber', 'bannerImageFileId', 'bannerImageFile', 'meetingSchedulingUrl']
            },
            userIdentity: {
              select: ['id', 'email', 'displayName', 'emailValidated']
            }
          }
        },
        projects: {
          select: ['id', 'enterpriseProfileId', 'name', 'projectManagerName', 'startDate', 'objectives'],
          expand: {
            serviceRequests: {
              select: [
                'id',
                'roleId',
                'tentativeStartDate',
                'tentativeEndDate',
                'serviceDurationInMonths',
                'description',
                'isDraft',
                'isActive',
                'notificationsFrequency',
                'state',
                'location',
                'locationWorkPreference',
                'roleExperience'
              ],
              expand: {
                role: { select: ['id', 'name'] },
                skills: {
                  select: ['skill'],
                  expand: { skill: { select: ['id', 'name'] } }
                }
              }
            }
          }
        }
      }
    }
  ): Promise<EnterpriseProfile> {
    return this.getOneWithOData(options, undefined, 'getProfile');
  }

  public getEnterpriseUsers(
    profileId: string,
    options: Partial<QueryOptions<EnterpriseUser>> = {
      select: [
        'userIdentityId',
        'fullName',
        'phoneNumber',
        'isAdmin',
        'registrationCompleted',
        'active',
        'profileImageFileId',
        'receiveEmailNotifications'
      ],
      expand: {
        userIdentity: {
          select: ['id', 'email', 'displayName', 'emailValidated']
        }
      }
    },
    params?: { [key: string]: any }
  ): Promise<ODataResult<EnterpriseUser>> {
    return this.getWithOData(options, params, `${encodeURIComponent(profileId)}/EnterpriseUsers`);
  }

  public async getCloseServiceRequestPreview(profileId: string, serviceRequestId: string): Promise<ItemCommandResult<ServiceRequestClosePreview>> {
    try {
      return await this.get<ItemCommandResult<ServiceRequestClosePreview>>(
        `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}/close-preview`
      );
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<ServiceRequestClosePreview>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public getEnterpriseProjects(
    profileId: string,
    options: Partial<QueryOptions<Project>> = {
      select: ['enterpriseProfileId', 'name', 'objectives', 'projectManagerName', 'startDate', 'sustainableDevelopmentGoals'],
      expand: {
        enterpriseProfile: {
          select: ['id', 'name']
        },
        serviceRequests: {
          select: enterpriseServiceRequestSelect
        },
        enterpriseUser: {
          select: ['id', 'userIdentityId', 'fullName'] as any,
          expand: {
            userIdentity: {
              select: ['id', 'email', 'displayName', 'emailValidated']
            }
          }
        }
      }
    },
    params?: { [key: string]: any },
    useWebApi?: boolean
  ): Promise<ODataResult<Project>> {
    return this.getWithOData(options, params, `${encodeURIComponent(profileId)}/Projects`, useWebApi);
  }

  public getEnterpriseProject(
    profileId: string,
    projectId: string,
    options: { select?: Select<Project>; expand?: Expand<Project> } = {
      select: ['enterpriseProfileId', 'name', 'objectives', 'projectManagerName', 'startDate', 'sustainableDevelopmentGoals'],
      expand: {
        enterpriseProfile: {
          select: ['id', 'name']
        },
        enterpriseUser: {
          select: ['id', 'userIdentityId', 'fullName'] as any,
          expand: {
            userIdentity: {
              select: ['id', 'email', 'displayName', 'emailValidated']
            }
          }
        },
        serviceRequests: {
          select: enterpriseServiceRequestSelect
        }
      }
    },
    params?: { [key: string]: any }
  ): Promise<Project> {
    return this.getOneWithOData(options, params, `${encodeURIComponent(profileId)}/Project/${encodeURIComponent(projectId)}`);
  }

  public getEnterpriseServiceRequests(
    profileId: string,
    options: Partial<QueryOptions<ServiceRequest>> = {
      expand: {
        project: {},
        role: {},
        skills: {}
      }
    },
    params?: { [key: string]: any },
    useWebApi?: boolean
  ): Promise<ODataResult<ServiceRequest>> {
    return this.getWithOData(withSafeServiceRequestSelect(options), params, `${encodeURIComponent(profileId)}/ServiceRequests`, useWebApi);
  }

  public getEnterpriseServiceRequest(
    profileId: string,
    requestId: string,
    options: { select?: Select<ServiceRequest>; expand?: Expand<ServiceRequest>; filter?: Filter<ServiceRequest> } = {
      expand: {
        project: {},
        role: {},
        skills: {}
      }
    },
    params?: { [key: string]: any },
    useWebApi?: boolean
  ): Promise<ServiceRequest> {
    return this.getOneWithOData(
      withSafeServiceRequestSelect(options),
      params,
      `${encodeURIComponent(profileId)}/ServiceRequest(key2=${encodeURIComponent(requestId)})`,
      useWebApi
    );
  }

  public async getReviews(
    profileId: string,
    options?: { select?: Select<EnterpriseReview>; expand?: Expand<EnterpriseReview>; count?: boolean; top?: number; skip?: number },
    params?: { [key: string]: any }
  ): Promise<ODataResult<EnterpriseReview>> {
    return this.getWithOData(options, params, `${encodeURIComponent(profileId)}/reviews`);
  }

  public async getMatchesGroupedByPerson(
    profileId: string,
    requestId: string,
    params: { statusGroup?: MatchStatusGroup; skip?: number; top?: number } = {}
  ): Promise<{ items: MatchPersonGroup[]; count: number }> {
    const { statusGroup, skip = 0, top = 10 } = params;
    const query: Record<string, any> = { skip, top };
    if (statusGroup) query.statusGroup = statusGroup;

    const res = (await this.get(
      `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(requestId)}/Matches/GroupedByPerson`,
      query
    )) as any;
    return {
      items: (res?.items ?? []) as MatchPersonGroup[],
      count: Number(res?.count ?? res?.items?.length ?? 0)
    };
  }
}

export class EnterpriseProfileFormApiService extends FormApiService {
  constructor() {
    super('enterpriseProfiles');
  }

  public async upsertEnterpriseBasicInfo(body: UpsertEnterpriseBasicInfoCommand): Promise<CommandResult> {
    try {
      return await this.put(body, 'upsertEnterpriseBasicInfo');
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertEnterpriseAvatarImages(adminImageFile?: RcFile, enterpriseImageFile?: RcFile): Promise<CommandResult> {
    try {
      const data: any = {};
      if (adminImageFile != null) data['adminImageFile'] = adminImageFile;
      if (enterpriseImageFile != null) data['enterpriseImageFile'] = enterpriseImageFile;
      return await this.put(data, 'upsertEnterpriseAvatarImages', true);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async update(profileId: string, body: UpdateEnterpriseProfileInfoBody): Promise<CommandResult> {
    try {
      return await this.put(body, `${encodeURIComponent(profileId)}`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateEnterpriseUser(profileId: string, userId: string, body: UpdateEnterpriseUserProfileInfoBody): Promise<CommandResult> {
    try {
      return await this.put(body, `${encodeURIComponent(profileId)}/EnterpriseUsers/${encodeURIComponent(userId)}`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertEnterpriseUserPhoneNumber(
    profileId: string,
    userId: string,
    body: { phoneNumber: string; phoneNumberCountryId: number }
  ): Promise<CommandResult> {
    try {
      return await this.put(body, `${encodeURIComponent(profileId)}/EnterpriseUsers/${encodeURIComponent(userId)}/upsertPhoneNumber`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async setEnterpriseUserActive(profileId: string, userId: string, active: boolean): Promise<CommandResult> {
    try {
      return await this.put({ active }, `${encodeURIComponent(profileId)}/EnterpriseUsers/${encodeURIComponent(userId)}/SetActive`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async setEnterpriseUserReceiveEmails(profileId: string, userId: string, receiveEmails: boolean): Promise<CommandResult> {
    try {
      return await this.put({ receiveEmails }, `${encodeURIComponent(profileId)}/EnterpriseUsers/${encodeURIComponent(userId)}/ReceiveEmails`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async setEnterpriseUserRegistrationCompleted(profileId: string, userId: string, registrationCompleted: boolean): Promise<CommandResult> {
    try {
      return await this.put(
        { registrationCompleted },
        `${encodeURIComponent(profileId)}/EnterpriseUsers/${encodeURIComponent(userId)}/SetRegistrationCompleted`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async addEnterpriseUser(profileId: string, body: AddEnterpriseUserCommandBody): Promise<ItemCommandResult<EnterpriseUserDto>> {
    try {
      return await this.post(body, `${encodeURIComponent(profileId)}/EnterpriseUsers`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<EnterpriseUserDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async deleteEnterpriseUser(profileId: string, userId: string): Promise<CommandResult> {
    try {
      return await this.delete(userId, `${encodeURIComponent(profileId)}/EnterpriseUsers`, {});
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async setEnterpriseUserOnboardingCompleted(enterpriseProfileId: string, enterpriseUserId: string): Promise<CommandResult> {
    try {
      return await this.put(
        {},
        `${encodeURIComponent(enterpriseProfileId)}/EnterpriseUsers/${encodeURIComponent(enterpriseUserId)}/completeOnboarding`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async createProjectWithRequestsBody(profileId: string, body: CreateProjectWithRequestsBody): Promise<ItemCommandResult<Project>> {
    try {
      return await this.post(body, `${encodeURIComponent(profileId)}/AddProjectWithRequests`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<Project>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateProject(profileId: string, projectId: string, data: UpdateProjectBody): Promise<ItemCommandResult<Project>> {
    try {
      return await this.put(data, `${encodeURIComponent(profileId)}/Project/${encodeURIComponent(projectId)}`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<Project>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertProject(profileId: string, data: UpsertProjectBody): Promise<ItemCommandResult<Project>> {
    try {
      return await this.post(data, `${encodeURIComponent(profileId)}/UpsertProject`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<Project>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async deleteServiceRequest(profileId: string, serviceRequestId: string, reason: string): Promise<CommandResult> {
    try {
      return await this.delete(serviceRequestId, `${encodeURIComponent(profileId)}/ServiceRequest`, { reason });
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async closeServiceRequest(profileId: string, serviceRequestId: string, body: CloseServiceRequestBody): Promise<CommandResult> {
    try {
      return await this.post(body, `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}/close`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async publishServiceRequest(
    profileId: string,
    serviceRequestId: string,
    notificationsFrequency: ServiceRequestNotificationFrequency
  ): Promise<CommandResult> {
    try {
      return await this.post(
        { notificationsFrequency },
        `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}/publish`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async reviewServiceRequest(profileId: string, serviceRequestId: string): Promise<CommandResult> {
    try {
      return await this.put({}, `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}/review`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateServiceRequestActive(profileId: string, serviceRequestId: string, isActive: boolean): Promise<CommandResult> {
    try {
      return await this.put({ isActive }, `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}/active`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateServiceRequestNotificationsConfig(
    profileId: string,
    serviceRequestId: string,
    notificationsFrequency: ServiceRequestNotificationFrequency
  ): Promise<CommandResult> {
    try {
      return await this.put(
        { notificationsFrequency },
        `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}/notifications`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateServiceRequest(
    profileId: string,
    serviceRequestId: string,
    data: UpdateServiceRequestDto
  ): Promise<ItemCommandResult<ServiceRequest>> {
    try {
      return await this.put(data, `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<ServiceRequest>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateServiceRequestSkills(
    profileId: string,
    requestId: string,
    skills: ServiceRequestSkillDto[]
  ): Promise<ItemCommandResult<ServiceRequestJobRoleSkill[]>> {
    try {
      return await this.post({ skills }, `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(requestId)}/Skills`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<ServiceRequestJobRoleSkill[]>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertAccountCancellationRequest(
    enterpriseProfileId: string,
    userId: string,
    body: { reason: EnterpriseCancelAccountReason; comment: string; newAdminId: string | null }
  ) {
    try {
      return await this.put<{ reason: EnterpriseCancelAccountReason; comment: string; newAdminId: string | null }, CommandResult>(
        body,
        `${encodeURIComponent(enterpriseProfileId)}/EnterpriseUsers/${encodeURIComponent(userId)}/UpsertAccountCancellationRequest`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async autofillFromLinkedin(enterpriseProfileId: string, linkedInUrl: string): Promise<ItemCommandResult<{ jobId: string }>> {
    try {
      return await this.post<{ linkedInUrl: string }, ItemCommandResult<{ jobId: string }>>(
        { linkedInUrl: linkedInUrl },
        `${encodeURIComponent(enterpriseProfileId)}/enqueueAutofillFromLinkedIn`
      );
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<{ jobId: string }>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async autofillFromLinkedinJobPost(enterpriseProfileId: string, linkedInJobPostUrl: string): Promise<ItemCommandResult<{ jobId: string }>> {
    try {
      return await this.post<{ linkedInJobPostUrl: string }, ItemCommandResult<{ jobId: string }>>(
        { linkedInJobPostUrl: linkedInJobPostUrl },
        `${encodeURIComponent(enterpriseProfileId)}/enqueueAutofillFromLinkedInJobPost`
      );
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<{ jobId: string }>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateCandidateFavoriteFromEnterprise(
    profileId: string,
    requestId: string,
    personProfileId: string,
    body: { favorite: boolean; statusGroup?: MatchStatusGroup }
  ): Promise<CommandResult> {
    try {
      return await this.put<{ favorite: boolean }, CommandResult>(
        body,
        `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(requestId)}/Candidates/${encodeURIComponent(personProfileId)}/Favorite`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async discardCandidateForServiceRequest(
    profileId: string,
    serviceRequestId: string,
    personProfileId: string,
    body?: DiscardCandidateForServiceRequestBody
  ): Promise<CommandResult> {
    const payload = {
      reason: body?.reason,
      reasonText: body?.reasonText ?? '',
      doNotAskAgain: body?.doNotAskAgain,
      statusGroup: body?.statusGroup
    };

    try {
      return await this.put(
        payload,
        `${encodeURIComponent(profileId)}/ServiceRequest/${encodeURIComponent(serviceRequestId)}/Candidates/${encodeURIComponent(
          personProfileId
        )}/Discard`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async saveOnboardingDraft(profileId: string, draftJson: string | null): Promise<CommandResult> {
    try {
      return await this.put({ draftJson }, `${encodeURIComponent(profileId)}/OnboardingDraft`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }
}
